import { normalizeTerminalPlanStatus } from "src/lib/normalizeTerminalPlanStatus";
import type { PlanData } from "src/stores/persistentChatStore";
import type { PersistentChatMessageData } from "~shared/types/persistent-chat-types";

/** "Accept all changes" appends a synthetic assistant message; it must not win plan sync over the real last model turn. */
export function getLastAssistantMessageForPlanSync(
  messages: PersistentChatMessageData[] | undefined,
): PersistentChatMessageData | undefined {
  return [...(messages || [])].reverse().find(
    (m) => m.role === "assistant" && m.metadata?.type !== "accept_all_changes",
  );
}

/**
 * Resolves persisted plan metadata for client/store sync.
 * When a plan is executing, the server updates the original plan message row; later assistant turns
 * often omit `metadata.plan`. Prefer the newest assistant message that carries this plan id.
 */
export function resolvePersistedPlanForSync(
  messages: PersistentChatMessageData[] | undefined,
  activePlanId: string | null | undefined,
): PlanData | null {
  if (!messages?.length) return null;

  if (activePlanId) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "assistant" || m.metadata?.type === "accept_all_changes") continue;
      const raw = m.metadata?.plan as PlanData | undefined;
      if (raw?.id === activePlanId) {
        return normalizeTerminalPlanStatus(raw);
      }
    }
  }

  const lastAssistant = getLastAssistantMessageForPlanSync(messages);
  const persistedPlan = lastAssistant?.metadata?.plan as PlanData | undefined;
  return persistedPlan ? normalizeTerminalPlanStatus(persistedPlan) : null;
}
